/**
 * User Result Card Component
 * Displays a user profile in search results
 */

import { useState } from 'react'
import { Card, CardContent, CardActionArea, Box, Typography, Chip, Avatar, IconButton, Tooltip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  LocationOn as LocationIcon,
  Message as MessageIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  PersonRemove as PersonRemoveIcon,
  HourglassBottom as HourglassIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import type { UserSearchResult } from '../../../api/types/search'
import { UserConnectionStatus } from '../../../api/types/search'

interface UserResultCardProps {
  user: UserSearchResult
}

export function UserResultCard({ user }: UserResultCardProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Initialize local connection status based on API response
  const getInitialStatus = (): 'connected' | 'pending' | 'none' | 'blocked' => {
    if (user.connectionStatus === UserConnectionStatus.Connected) return 'connected'
    if (user.connectionStatus === UserConnectionStatus.Pending) return 'pending'
    if (user.connectionStatus === UserConnectionStatus.Blocked) return 'blocked'
    return 'none'
  }

  const [localConnectionStatus, setLocalConnectionStatus] = useState<'connected' | 'pending' | 'none' | 'blocked'>(
    getInitialStatus()
  )

  const handleClick = () => {
    navigate(`/profile/${user.userId}`)
  }

  // Send friend request mutation
  const sendFriendRequestMutation = useMutation({
    mutationFn: () => socialApi.sendFriendRequest({ friendId: user.userId }),
    onSuccess: () => {
      setLocalConnectionStatus('pending')
      queryClient.invalidateQueries({ queryKey: ['friends'] })
    },
  })

  // Remove friend mutation
  const removeFriendMutation = useMutation({
    mutationFn: () => socialApi.removeFriend(user.userId),
    onSuccess: () => {
      setLocalConnectionStatus('none')
      queryClient.invalidateQueries({ queryKey: ['friends'] })
    },
  })

  // Block user mutation
  const blockUserMutation = useMutation({
    mutationFn: () => socialApi.blockUser({ userId: user.userId }),
    onSuccess: () => {
      setLocalConnectionStatus('none')
      queryClient.invalidateQueries({ queryKey: ['friends'] })
      queryClient.invalidateQueries({ queryKey: ['search'] })
    },
  })

  const handleMessage = (e: React.MouseEvent) => {
    e.stopPropagation()
    // TODO: Implement messaging
    console.log('Message user:', user.userId)
  }

  const handleFriendAction = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (localConnectionStatus === 'connected') {
      removeFriendMutation.mutate()
    } else if (localConnectionStatus === 'none') {
      sendFriendRequestMutation.mutate()
    }
  }

  const handleBlock = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm(`Are you sure you want to block ${user.displayName}?`)) {
      blockUserMutation.mutate()
    }
  }

  const getConnectionStatusColor = () => {
    if (localConnectionStatus === 'connected') return 'success'
    if (localConnectionStatus === 'pending') return 'warning'
    if (localConnectionStatus === 'blocked') return 'error'
    return 'default'
  }

  const getConnectionStatusLabel = () => {
    if (localConnectionStatus === 'connected') return 'Connected'
    if (localConnectionStatus === 'pending') return 'Pending'
    if (localConnectionStatus === 'blocked') return 'Blocked'
    return 'Not Connected'
  }

  return (
    <Box sx={{ position: 'relative', mb: 2, mt: 3 }}>
      {/* Type Badge - Positioned above card */}
      <Chip
        label="User"
        size="small"
        color="secondary"
        sx={{
          position: 'absolute',
          top: -10,
          left: 16,
          zIndex: 1,
        }}
      />
      <Card
        sx={{
          mt: 0,
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          {/* User Info Row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Avatar with Online Status - Clickable */}
            <Box onClick={handleClick} sx={{ cursor: 'pointer' }}>
                <Avatar
                  src={user.avatar || user.profilePicture}
                  alt={user.displayName}
                  sx={{ width: 56, height: 56 }}
                >
                  {user.displayName?.charAt(0).toUpperCase()}
                </Avatar>
            </Box>

            {/* User Details - Clickable */}
            <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={handleClick}>
              {/* Display Name with Rank */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6" component="h3">
                  {user.displayName}
                </Typography>
                {user.rank?.name && (
                  <Chip
                    label={user.rank.name}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={
                      user.rank.cssClass
                        ? {
                            fontWeight: 'bold',
                          }
                        : undefined
                    }
                  />
                )}
              </Box>

              {/* Location */}
              {(user.location || user.homeTown) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {user.location || user.homeTown}
                  </Typography>
                </Box>
              )}

              {/* Bio */}
              {user.bio && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mt: 0.5,
                  }}
                >
                  {user.bio}
                </Typography>
              )}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Tooltip title="Send Message">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleMessage}
                  sx={{ bgcolor: 'action.hover' }}
                >
                  <MessageIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={localConnectionStatus === 'connected' ? 'Unfriend' : localConnectionStatus === 'pending' ? 'Request Pending' : 'Add Friend'}>
                <span>
                  <IconButton
                    size="small"
                    color={localConnectionStatus === 'connected' ? 'error' : localConnectionStatus === 'pending' ? 'warning' : 'success'}
                    onClick={handleFriendAction}
                    disabled={sendFriendRequestMutation.isPending || removeFriendMutation.isPending || localConnectionStatus === 'pending'}
                    sx={{ bgcolor: 'action.hover' }}
                  >
                    {localConnectionStatus === 'connected' ? (
                      <PersonRemoveIcon fontSize="small" />
                    ) : localConnectionStatus === 'pending' ? (
                      <HourglassIcon fontSize="small" />
                    ) : (
                      <PersonAddIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Block User">
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleBlock}
                  disabled={blockUserMutation.isPending}
                  sx={{ bgcolor: 'action.hover' }}
                >
                  <BlockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Status Row */}
        </CardContent>
    </Card>
    </Box>
  )
}
